import { Box, Button, MenuItem } from "@material-ui/core"
import { equals } from "ramda"
import { useEffect } from "react"
import type { ChangeEvent, ReactElement } from "react"
import { useForm } from "react-hook-form"

import type { Chapter, Licence, Region, Theme } from "../../../redux"
import { TextInputField } from "../../atoms"
import { FormValidationErrors, LicenceField } from "../../molecules"

import "./theme-manager-form.styles.scss"
import { useTranslation } from "react-i18next"

export interface IThemeManagerFormProps {
  theme: Theme
  chapters: Chapter[]
  regions: Region[]
  licences: Licence[]
  onLicenceIdsChanged: (licenceIds: string[]) => void
  onChapterChange: (chapterId: string, licenceIds: string[]) => void
  saveTheme: (subTheme: Theme) => void
}

export function ThemeManagerForm(props: IThemeManagerFormProps): ReactElement {
  const { theme, chapters, regions, licences, onChapterChange, onLicenceIdsChanged, saveTheme } = props
  const {
    control,
    formState: { errors, submitCount },
    handleSubmit,
    getValues,
    setValue,
    watch,
    reset
  } = useForm<Theme>({
    defaultValues: theme
  })

  const { t } = useTranslation("contentManager")
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    const chapterId: string | undefined = getValues("chapterId")
    if (chapters.length > 0 && (!chapterId || chapterId.length === 0)) {
      setValue("chapterId", chapters[0].id, { shouldValidate: true })
    } else if (chapters.length > 0 && chapterId && chapterId.length > 0) {
      setValue("chapterId", chapterId)
    }
  }, [chapters])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (theme && !formSynced()) reset(theme)
  }, [theme])

  function formSynced(): boolean {
    return equals(getValues(), theme)
  }

  function chapterIdChanged(
    changeHandler: (event: ChangeEvent<HTMLInputElement>) => void
  ): (event: ChangeEvent<HTMLInputElement>) => void {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const chapterId: string = event.target.value
      setValue("regionId", " ")
      onChapterChange(chapterId, getValues("licenceIds"))
      changeHandler(event)
    }
  }

  function onSubmit(data: Theme): void {
    saveTheme(data)
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    const licenceIds: string[] = getValues("licenceIds")
    if (licenceIds !== theme.licenceIds) {
      reset({ ...getValues(), chapterId: " ", regionId: " " })
    }
    if (licenceIds.length > 0) {
      onLicenceIdsChanged(licenceIds)
    }
  }, [watch("licenceIds")])

  function hasLicenceSelected(): boolean {
    return getValues("licenceIds").length > 0
  }

  return (
    <form className="theme-manager-form" onSubmit={handleSubmit(onSubmit)}>
      <LicenceField
        name="licenceIds"
        control={control}
        required={t("commonFormfields.requiredLicence")}
        label={t("commonFormfields.licences")}
        className="licence-field"
        licences={licences}
      />
      <TextInputField
        name="name"
        control={control}
        required={t("themes.formFields.requiredThemeName")}
        label={t("themes.formFields.themeTitle")}
        disabled={!hasLicenceSelected()}
      />
      <Box className="grouped-fields">
        <TextInputField
          name="order"
          control={control}
          required={t("commonFormfields.requiredOrderTableContents")}
          label={t("commonFormfields.orderTableContents")}
          disabled={!hasLicenceSelected()}
          type="number"
        />
        <TextInputField
          name="chapterId"
          control={control}
          label={t("commonFormfields.chapter")}
          minLength={{
            value: 4,
            message: t("commonFormfields.requiredChapter")
          }}
          select
          required={t("commonFormfields.requiredChapter")}
          disabled={!hasLicenceSelected()}
          onSelect={chapterIdChanged}
        >
          <MenuItem value=" ">{t("commonFormfields.noChapter")}</MenuItem>
          {chapters.map((chapter: Chapter) => (
            <MenuItem key={chapter.id} value={chapter.id}>
              {chapter.name}
            </MenuItem>
          ))}
        </TextInputField>
        <TextInputField
          name="regionId"
          control={control}
          label={t("themes.formFields.area")}
          select
          selectProps={{ displayEmpty: true }}
          disabled={!hasLicenceSelected()}
        >
          <MenuItem value=" ">{t("themes.formFields.noArea")}</MenuItem>
          {regions.map((region: Region) => (
            <MenuItem key={region.id} value={region.id}>
              {region.name}
            </MenuItem>
          ))}
        </TextInputField>
      </Box>

      <Button type="submit" variant="contained" color="primary" disabled={!hasLicenceSelected()}>
        {t("save")}
      </Button>
      <FormValidationErrors errors={errors} submitCount={submitCount} />
    </form>
  )
}
